/**
 * Aplikacione Konstante
 * 
 * Centralizovane konstante koje se koriste širom aplikacije.
 * Organizovane po kategorijama za lakše održavanje.
 */

// ============================================================================
// DATE & TIME FORMATS
// ============================================================================

/**
 * Date format za backend komunikaciju (MySQL, Spring Boot)
 * Format: YYYY-MM-DD
 */
export const BACKEND_DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Date format za frontend prikaz (srpski standard)
 * Format: DD.MM.YYYY
 */
export const FRONTEND_DATE_FORMAT = 'DD.MM.YYYY';

/**
 * DateTime format za backend komunikaciju
 * Format: YYYY-MM-DD HH:mm
 */
export const BACKEND_DATETIME_FORMAT = 'YYYY-MM-DD HH:mm';

/**
 * DateTime format za frontend prikaz
 * Format: DD.MM.YYYY HH:mm
 */
export const FRONTEND_DATETIME_FORMAT = 'DD.MM.YYYY HH:mm';

// ============================================================================
// USER ROLES
// ============================================================================

/**
 * User role IDs - mapiranje između imena uloge i ID-a
 * 
 * Koristi se za:
 * - Authorization provere
 * - Conditional rendering
 * - API requests (X-Role header)
 */
export const USER_ROLES = {
  ANONYMOUS: 0,
  ADMIN: 1,
  CITIZEN: 3,
  EVALUATOR: 4,
  OGP_ADMIN: 5,
};

// ============================================================================
// AUTHENTICATION & AUTHORIZATION
// ============================================================================

/**
 * eID Authentication Assurance Levels
 * Minimalni i maksimalni nivo sigurnosti za eID prijavu
 */
export const AUTH_ASSURANCE_LEVELS = {
  MIN: 'AAL1',
  MAX: 'AAL3',
};

/**
 * Session Storage Keys
 * NAPOMENA: Dinamički generisan ključ na osnovu CLIENT_ID
 */
export const STORAGE_KEYS = {
  // Generiše se u runtime-u: `oidc.user:${STS_AUTHORITY}:${CLIENT_ID}`
  OIDC_USER: 'oidc.user',
  // Custom keys
  USER_PREFERENCES: 'user_preferences',
  THEME: 'app_theme',
};

// ============================================================================
// FILE HANDLING
// ============================================================================

/**
 * Dozvoljeni formati fajlova za upload
 */
export const ALLOWED_FILE_FORMATS = '.pdf, .png, .jpg, .gif, .xlsx, .csv, .docx, .doc, .txt';

/**
 * Dozvoljeni formati kao array (za validaciju)
 */
export const ALLOWED_FILE_EXTENSIONS = [
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 
  'xlsx', 'xls', 'csv', 
  'docx', 'doc', 'txt'
];

/**
 * MIME tipovi za validaciju fajlova
 */
export const MIME_TYPES = {
  PDF: 'application/pdf',
  PNG: 'image/png',
  JPG: 'image/jpeg',
  JPEG: 'image/jpeg',
  GIF: 'image/gif',
  XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  XLS: 'application/vnd.ms-excel',
  CSV: 'text/csv',
  DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  DOC: 'application/msword',
  TXT: 'text/plain',
};

/**
 * Maksimalne veličine fajlova (u bajtovima)
 */
export const FILE_SIZE_LIMITS = {
  IMAGE: 5 * 1024 * 1024,       // 5 MB
  DOCUMENT: 10 * 1024 * 1024,   // 10 MB
  PDF: 20 * 1024 * 1024,        // 20 MB
  EXCEL: 10 * 1024 * 1024,      // 10 MB
};

// ============================================================================
// PAGINATION
// ============================================================================

/**
 * Opcije za broj redova po stranici (za tabele)
 */
export const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

/**
 * Default broj redova po stranici
 */
export const DEFAULT_ROWS_PER_PAGE = 10;

/**
 * Default pagination config
 */
export const DEFAULT_PAGINATION = {
  page: 0,
  size: DEFAULT_ROWS_PER_PAGE,
  sort: 'id,desc',
};

// ============================================================================
// ERROR MESSAGES
// ============================================================================

/**
 * Standardne error poruke (na srpskom)
 */
export const ERROR_MESSAGES = {
  ENTITY_IN_USE: 'Ентитет је у употреби и не може бити обрисан!',
  NETWORK_ERROR: 'Грешка у комуникацији са сервером',
  UNAUTHORIZED: 'Немате дозволу за ову акцију',
  SESSION_EXPIRED: 'Ваша сесија је истекла. Молимо пријавите се поново.',
  INVALID_FILE_FORMAT: 'Неисправан формат фајла',
  FILE_TOO_LARGE: 'Фајл је превелик',
  REQUIRED_FIELD: 'Ово поље је обавезно',
  INVALID_DATE: 'Неисправан датум',
  GENERIC_ERROR: 'Дошло је до грешке. Молимо покушајте поново.',
};

/**
 * Success poruke
 */
export const SUCCESS_MESSAGES = {
  SAVE_SUCCESS: 'Успешно сачувано',
  DELETE_SUCCESS: 'Успешно обрисано',
  UPDATE_SUCCESS: 'Успешно ажурирано',
  UPLOAD_SUCCESS: 'Фајл је успешно отпремљен',
};

// ============================================================================
// ANALYTICS
// ============================================================================

/** Google Analytics tracking ID iz REACT_APP_GA_KEY (.env); prazan string = isključeno. */
export const GA_TRACKING_ID = (process.env.REACT_APP_GA_KEY || '').trim();

// ============================================================================
// DOCUMENT TITLES (za Help/Manual sistem)
// ============================================================================

/**
 * Nazivi PDF/DOCX dokumenata za različite korisničke uloge
 * Koristi se u Help/Manual sistemu
 */
export const DOCUMENT_TITLES = {
  ANONYMOUS_USER: 'AnonimniKorisnik.pdf',
  ROLE_ADMIN: 'Admin.pdf',
  ROLE_ADMIN_OGP: 'AdminOGP.pdf',
  ROLE_EVALUATOR: 'Evaluator.pdf',
  ROLE_CITIZEN: 'Gradjanin.pdf',
  ROAD_OF_LAW: 'Put usvajanja zakona.docx',
  PRIVACY_POLICY: 'PolitikaPrivatnosti.docx',
};

/**
 * Manual opcije - za dropdown/select u Help sistemu
 */
export const MANUAL_OPTIONS = [
  { value: 0, label: 'АНОНИМНИ КОРИСНИК' },
  { value: 1, label: 'АДМИН' },
  { value: 3, label: 'ГРАЂАНИН' },
  { value: 4, label: 'ЕВАЛУАТОР' },
  { value: 5, label: 'ОГП АДМИН' },
  { value: 6, label: 'ПОЛИТИКА ПРИВАТНОСТИ' },
];

/**
 * Modal types za Header komponente
 */
export const MODAL_TYPES = {
  MANUAL: 'MANUAL',
  ROAD_OF_LAW: 'ROAD_OF_LAW',
  PRIVACY_POLICY: 'PRIVACY_POLICY',
};

// ============================================================================
// API CONFIGURATION
// ============================================================================

/**
 * API Request timeout (milisekunde)
 */
export const API_TIMEOUT = parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000;

/**
 * HTTP Status Codes (za referencu)
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
};

// ============================================================================
// OGP SPECIFIC CONSTANTS
// ============================================================================

/**
 * OGP Commitment statusi
 */
export const OGP_COMMITMENT_STATUS = {
  DRAFT: 'draft',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
};

/**
 * OGP Challenge types
 */
export const OGP_CHALLENGE_TYPES = {
  ACCESS_TO_INFORMATION: 'access_to_information',
  CIVIC_PARTICIPATION: 'civic_participation',
  PUBLIC_ACCOUNTABILITY: 'public_accountability',
  TECHNOLOGY_AND_INNOVATION: 'technology_and_innovation',
};

// ============================================================================
// UI CONSTANTS
// ============================================================================

/**
 * Tailwind breakpoints (za referencu)
 */
export const BREAKPOINTS = {
  SM: 640,
  MD: 768,
  LG: 1024,
  XL: 1280,
  '2XL': 1536,
};

/**
 * Toast notification duration (milisekunde)
 */
export const TOAST_DURATION = {
  SHORT: 2000,
  NORMAL: 4000,
  LONG: 6000,
  EXTENDED: 30000,
};

/**
 * Loading states
 */
export const LOADING_STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

// ============================================================================
// FEATURE FLAGS
// ============================================================================

/**
 * Feature flags (omogućava/onemogućava features u runtime-u)
 */
export const FEATURE_FLAGS = {
  ENABLE_A11Y_CHECK: process.env.REACT_APP_ENABLE_A11Y_CHECK === 'true',
  ENABLE_OGP_MODULE: true, // Uvek omogućeno u ovoj verziji
  ENABLE_SURVEYS: true,
  ENABLE_COMMENTS: true,
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Regex patterns za validaciju
 */
export const VALIDATION_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^(\+381|0)[0-9]{8,9}$/,
  JMBG: /^[0-9]{13}$/,
  URL: /^https?:\/\/.+/,
};

/**
 * Validation lengths
 */
export const VALIDATION_LENGTHS = {
  MIN_PASSWORD: 8,
  MAX_PASSWORD: 128,
  MIN_USERNAME: 3,
  MAX_USERNAME: 50,
  MAX_DESCRIPTION: 5000,
  MAX_TITLE: 255,
  MAX_COMMENT: 2000,
};

// ============================================================================
// EXPORTS
// ============================================================================

/**
 * Default export za convenience
 */
const constants = {
  // Date & Time
  BACKEND_DATE_FORMAT,
  FRONTEND_DATE_FORMAT,
  BACKEND_DATETIME_FORMAT,
  FRONTEND_DATETIME_FORMAT,
  
  // User Roles
  USER_ROLES,
  
  // Auth
  AUTH_ASSURANCE_LEVELS,
  STORAGE_KEYS,
  
  // Files
  ALLOWED_FILE_FORMATS,
  ALLOWED_FILE_EXTENSIONS,
  MIME_TYPES,
  FILE_SIZE_LIMITS,
  
  // Pagination
  ROWS_PER_PAGE_OPTIONS,
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_PAGINATION,
  
  // Messages
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  
  // Analytics
  GA_TRACKING_ID,
  
  // Documents
  DOCUMENT_TITLES,
  MANUAL_OPTIONS,
  MODAL_TYPES,
  
  // API
  API_TIMEOUT,
  HTTP_STATUS,
  
  // OGP
  OGP_COMMITMENT_STATUS,
  OGP_CHALLENGE_TYPES,
  
  // UI
  BREAKPOINTS,
  TOAST_DURATION,
  LOADING_STATES,
  
  // Features
  FEATURE_FLAGS,
  
  // Validation
  VALIDATION_PATTERNS,
  VALIDATION_LENGTHS,
};

export default constants;

